import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AuthHero } from '../../components/AuthHero';
import { isNative } from '../../capacitorInit';

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(name, email, password, acceptedTerms);
      localStorage.setItem('carousel_tour_pending', 'true');
      navigate('/mfa-setup');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Registration failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col desktop:flex-row">
      {/* Left: marketing hero — full on desktop, compact on mobile */}
      <AuthHero />

      {/* Right: registration form */}
      <div className={`flex-1 desktop:flex-none desktop:w-[42%] flex ${isNative ? 'items-start overflow-y-auto' : 'items-center'} justify-center p-6 lg:p-10 bg-[#F8F9FF]`}>
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 w-full p-8 lg:p-10">
          <h2 className="text-2xl font-serif font-bold text-slate-900 mb-1">Create your archive</h2>
          <p className="text-stone-500 text-sm mb-6">Free to start — your family's stories, preserved forever.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Your name</label>
              <input
                type="text" value={name} onChange={e => setName(e.target.value)}
                required autoFocus
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                required
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Password</label>
              <input
                type="password" value={password} onChange={e => setPassword(e.target.value)}
                required minLength={8}
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-stone-400 mt-1">Minimum 8 characters</p>
            </div>

            <label className="flex items-start gap-2 text-sm text-stone-600">
              <input
                type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)}
                required
                className="mt-0.5 rounded border-stone-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span>
                I agree to the{' '}
                <Link to="/terms" className="text-indigo-600 hover:underline">Terms of Use</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link>,
                including the zero-tolerance policy for abusive behavior.
              </span>
            </label>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <button
              type="submit" disabled={loading || !acceptedTerms}
              className="w-full bg-indigo-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-stone-500">
            Already have an account?{' '}
            <Link to="/login" className="text-indigo-600 font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
